import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Form } from '@unform/web';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { Spinner } from '~/components';
import { Select, Input } from '~/components/Form';
import { MODULO_CTF } from '~/constants/modules';
import { t, validCharacters, can } from '~/helpers';
import { storeData, updateData } from '~/store/modules/empresacliente/actions';

export default function CreateOrUpdate({
  reference,
  data,
  create,
  toggleConfirmButton,
}) {
  const dispatch = useDispatch();

  const { client } = useSelector((state) => state.user.profile);
  const { loading, find, params } = useSelector(
    (state) => state.empresacliente
  );

  const initials = create ? data : { ...find, ...data };

  async function handleSubmit(param) {
    try {
      const schema = Yup.object().shape({
        idempresaveiculo: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .max(11, t('s.geral.validacao.tamanhomaximo').replace('%s', 11))
          .test(
            'characters-invalid',
            t('s.configuracao.geral.naopodecontercaracterespecial'),
            (value) => validCharacters(value)
          ),
        nomeempresaveiculo: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .max(30, t('s.geral.validacao.tamanhomaximo').replace('%s', 30))
          .test(
            'characters-invalid',
            t('s.configuracao.geral.naopodecontercaracterespecial'),
            (value) => validCharacters(value)
          ),
        statusempresaveiculo: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .max(1, t('s.geral.validacao.tamanhomaximo').replace('%s', 1)),
        controlacotactfempresaveiculo: Yup.number(),
      });

      await schema.validate(param, {
        abortEarly: false,
      });

      const objSend = {
        ...initials,
        ...param,
        idcliente: client,
      };

      if (create) {
        await dispatch(storeData(objSend, params));
      } else {
        await dispatch(updateData(objSend, params));
      }
    } catch (err) {
      const validationErrors = {};

      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message.replace(
            `${error.path} `,
            ''
          );
        });

        reference.current.setErrors(validationErrors);
      }
    }
  }

  useEffect(() => {
    if (!loading) {
      reference.current.setData(initials);
    }
  }, [loading, initials]);

  useEffect(() => {
    toggleConfirmButton(true);
  }, []);

  return (
    <>
      {loading ? (
        <div style={{ height: '14rem', width: '50rem' }}>
          <Spinner loading />
        </div>
      ) : (
        <Form ref={reference} initialData={initials} onSubmit={handleSubmit}>
          <Input
            name="idempresaveiculo"
            label={t('s.cadastro.empresaveiculo.idempresaveiculo')}
            variant="outlined"
            fullWidth
            size="small"
            disabled={!create}
            InputLabelProps={{
              shrink: true,
            }}
          />

          <Input
            name="nomeempresaveiculo"
            label={t('s.cadastro.empresaveiculo.nomeempresaveiculo')}
            variant="outlined"
            fullWidth
            size="small"
            InputLabelProps={{
              shrink: true,
            }}
          />

          <Select
            name="statusempresaveiculo"
            variant="outlined"
            label={t('s.cadastro.empresaveiculo.statusempresaveiculo')}
            options={[
              { value: 'L', label: t('s.geral.opcoes.liberado') },
              { value: 'B', label: t('s.geral.opcoes.bloqueado') },
              { value: 'C', label: t('s.geral.opcoes.cancelado') },
            ]}
          />

          {can(MODULO_CTF) && (
            <Select
              name="controlacotaempresaveiculo"
              variant="outlined"
              label={t('Controla Cota')}
              options={[
                { value: 0, label: t('Não') },
                { value: 1, label: t('Controla, mas abastece offline') },
                { value: 2, label: t('Controla e não abastece offline') },
              ]}
            />
          )}
        </Form>
      )}
    </>
  );
}

CreateOrUpdate.propTypes = {
  reference: PropTypes.objectOf(PropTypes.any).isRequired,
  create: PropTypes.bool.isRequired,
  data: PropTypes.objectOf(PropTypes.any),
  toggleConfirmButton: PropTypes.func.isRequired,
};

CreateOrUpdate.defaultProps = {
  data: {},
};
